import React, { useMemo } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import DividendPerShareChart from "./DividendPerShareChart";
import DividendYieldChart from "./DividendYieldChart";
import { DividendHistoryTable } from "./DividendHistoryTable";
import { DividendSummaryMetrics } from "./DividendSummaryMetrics";
import { useCompanyDividends, isDividendNotFoundError } from "../hooks/useCompanyDividends";
import { useCompanyRatios } from "../hooks/useCompanyRatios";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { roundedPanelSx } from "../../overviewPanel";

interface DividendsTabProps {
  ticker: string;
  price: number;
}

export const DividendsTab: React.FC<DividendsTabProps> = ({ ticker, price }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { formatPrice } = useStockCurrency();

  // Fetch data for summary metrics
  const { data: dividends, isLoading: dividendsLoading, error: dividendsError } = useCompanyDividends(ticker, 100);
  const { data: ratiosQuarterly, isLoading: ratiosLoading } = useCompanyRatios(ticker, 1, "quarter");

  const isLoading = dividendsLoading || ratiosLoading;
  const is404Error = isDividendNotFoundError(dividendsError);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const quarterlyRatios = Array.isArray(ratiosQuarterly) ? ratiosQuarterly[0] : ratiosQuarterly;

    // Filter out special dividends
    const regularDividends = dividends?.filter((d) => d.frequency !== "Special") ?? [];
    const latestRegularDividend = regularDividends[0];

    // Payout ratio (Quarterly - most recent quarter for freshest data)
    const payoutRatio = quarterlyRatios?.dividendPayoutRatio
      ? (quarterlyRatios.dividendPayoutRatio * 100).toFixed(1)
      : null;

    // Get frequency multiplier for annualizing dividends
    const getFrequencyMultiplier = (freq: string | null): number | null => {
      switch (freq) {
        case "Monthly": return 12;
        case "Quarterly": return 4;
        case "Semi-Annual": return 2;
        case "Annual": return 1;
        default: return null;
      }
    };

    const frequency = latestRegularDividend?.frequency ?? null;

    // Calculate trailing 12 months dividend (fallback)
    let trailingAnnualDPS: number | null = null;
    if (regularDividends.length > 0) {
      const oneYearAgo = dayjs().subtract(1, "year");
      const yearDividends = regularDividends.filter((d) =>
        dayjs(d.paymentDate).isAfter(oneYearAgo)
      );
      if (yearDividends.length > 0) {
        trailingAnnualDPS = yearDividends.reduce((sum, d) => sum + d.dividend, 0);
      }
    }

    // Forward/Indicated Annual DPS
    const multiplier = getFrequencyMultiplier(frequency);
    const mostRecentDividend = latestRegularDividend?.dividend ?? 0;
    const annualDPS = multiplier !== null && mostRecentDividend > 0
      ? mostRecentDividend * multiplier
      : trailingAnnualDPS;

    // Current dividend yield
    const currentYield =
      annualDPS !== null && price > 0
        ? ((annualDPS / price) * 100).toFixed(2)
        : null;

    // Latest dividend details
    const latestDividendAmount = latestRegularDividend?.dividend ?? null;

    return {
      currentYield,
      payoutRatio,
      annualDPS,
      latestDividendAmount,
      frequency,
    };
  }, [ratiosQuarterly, dividends, price]);

  // Check if company pays dividends (not a 404 and has data)
  const hasDividends = !is404Error && dividends && dividends.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Summary Metrics - Only show if has dividends or loading */}
      {(hasDividends || isLoading) && (
        <DividendSummaryMetrics
          currentYield={summaryMetrics.currentYield}
          payoutRatio={summaryMetrics.payoutRatio}
          latestDividend={
            summaryMetrics.latestDividendAmount !== null
              ? formatPrice(Math.floor(summaryMetrics.latestDividendAmount * 100) / 100, 2)
              : null
          }
          annualDividend={
            summaryMetrics.annualDPS !== null
              ? formatPrice(Math.floor(summaryMetrics.annualDPS * 100) / 100, 2)
              : null
          }
          frequency={summaryMetrics.frequency}
          isLoading={isLoading}
        />
      )}

      {/* Charts Grid - 2 columns on desktop, 1 column on mobile */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
          gap: 2,
        }}
      >
        <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
          <DividendPerShareChart
            ticker={ticker}
            height={240}
            renderCard={false}
            showFullscreen={true}
          />
        </Box>

        <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
          <DividendYieldChart
            ticker={ticker}
            price={price}
            height={240}
            renderCard={false}
            showFullscreen={true}
          />
        </Box>
      </Box>

      {/* Dividend History Table with Summary Metrics - Full Width */}
      <DividendHistoryTable ticker={ticker} price={price} />
    </Box>
  );
};

export default DividendsTab;
